import React from "react";
import {
  Box,
  Typography,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
  Stack,
  Grid,
} from "@mui/material";
import GenericBars from "./genericBars";
import StyledTooltip from "../../utils/styledToolTip";
import { formatDecimal } from "../../utils/numberFormat";

export default function HealthResults({ projects, forPdf=false }) {
  // Paleta focada em 3 projetos
  const palette = ["#7C3AED", "#22C55E", "#3B82F6"]; // roxo, verde, azul
  const colorForIndex = (i) => palette[i % palette.length];

  const METRICS = [
    {
      key: "moreActive",
      label: "Number of people more active",
      unit: "people",
      valuePath: "projectInfo.numberOfPeopleMoreActive",
      decimals: 0,
      tooltip: "Number of people that started making physical activity due to the actions."
    },
    {
      key: "lifeYearsGained",
      label: "Life years gained",
      unit: "years",
      valuePath: "projectInfo.lifeYearsGained",
      decimals: 2,
      tooltip: "The additional years of life gained, in total, by all new active people because some of their deaths are averted. They account for both the number of deaths averted and the age at which they would have occurred."
    },
    {
      key: "deathsAverted",
      label: "Deaths averted",
      unit: "deaths",
      valuePath: "projectInfo.deathsAverted",
      decimals: 0,
      tooltip: "An estimate of the number of deaths that are avoidable due to an intervention."
    },
    {
      key: "lifeExpectancyAtBirth",
      label: "Life expectancy at birth gained",
      unit: "years",
      valuePath: "projectInfo.lifeExpectancyAtBirth",
      decimals: 2,
      tooltip: "The average number of years gained that a newborn would live if current death rates do not change."
    },
  ];

  /* ---------- Helpers ---------- */
  const getByPath = (obj, path) => {
    if (!obj || !path) return undefined;
    return path.split(".").reduce((acc, key) => (acc == null ? acc : acc[key]), obj);
  };

  function getMetricValue(project, metric) {
    if (typeof metric.valueFn === "function") return metric.valueFn(project);
    //console.log("Metrix max", metric, "  Max value=", getByPath(project, metric.valuePath))
    if (metric.valuePath) return getByPath(project, metric.valuePath);
    return undefined;
  }

  function formatMetricValue(value, decimals = 2) {
    if (typeof value !== "number" || !Number.isFinite(value)) return "--";
    return formatDecimal(value, decimals);
  }

  function percentage(value, max) {
    //console.log("value=", value, "   max= ", max);
    const safeMax = max > 0 ? max : 1;
    const v = typeof value === "number" && Number.isFinite(value) ? value : 0;
    return Math.max(0, Math.min(100, (v / safeMax) * 100));
  }

  // 1x por render: dicionário de máximos por métrica (para a escala das barras)
  const maxDict = React.useMemo(() => {
    const dict = {};

    for (const m of METRICS) {
      let max = 0;

      for (const p of projects) {
        const raw = getMetricValue(p, m);
        const v = Number(raw);

        if (Number.isFinite(v) && v > max) {
          max = v;
        }
      }

      // evitar max = 0 → barras invisíveis
      dict[m.key] = max === 0 ? 1 : max;
    }

    return dict;
  }, [projects]);


  // (opcional) Se algumas métricas podem ser negativas, podes escolher:
  // - 'usar absoluto' para a barra (mas mostrar sinal no número)
  // - 'clamp a 0' (não mostrar barra para valores negativos)
  const toBarValue = (metricKey, v) => {
    // exemplo: para lifeYearsGained usar absoluto na barra
    //if (metricKey === "lifeYearsGained") return Math.abs(v || 0);
    //return v || 0;
    return Math.abs(v || 0);
  };

  const roundToTwo = (value) => Math.round((value + Number.EPSILON) * 100) / 100;

  return (
    <Box sx={{ mt: forPdf ? 0 : 6, pageBreakBefore: forPdf ? 'always' : 'auto' }}>
      {/* Header */}
      <Box display="flex" alignItems="center" sx={{ mb: 1 }}>
        <Typography variant="h6">Health</Typography>
        <Typography
          component="span"
          variant="body2"
          color="text.secondary"
          sx={{ ml: 1, cursor: "help" }}
        >
          <StyledTooltip title="Mortality and morbidity data" arrow><span className="icon"></span></StyledTooltip>
        </Typography>
      </Box>

      <Typography variant="subtitle1"
        sx={{textTransform: "uppercase", fontSize: "0.9em"}}>
          Mortality Reduction
      </Typography>

      {/* ---- Tabela com números ---- */}
      <TableContainer sx={{ width: "100%" }}>
        <Table stickyHeader sx={{ mt: 1.5, tableLayout: "fixed", width: "100%" }}>
          <TableHead>
            <TableRow
              sx={{
                position: "relative",
                "& > .MuiTableCell-root": {
                  borderBottom: "1px solid #e0e0e0 !important",
                  borderCollapse: "collapse",
                },
              }}
            >
              <TableCell sx={{ width: "100%" }}>
                <Typography
                  variant="subtitle1"
                  color="text.secondary"
                  align="left"
                  sx={{ fontWeight: 600, px: 0 }}
                >
                  Metric
                </Typography>
              </TableCell>

              {/* Coluna por projeto */}
              {projects.map((project) => (
                <TableCell
                  key={project.id ?? project.projectName}
                  align="right"
                  sx={{ width: 160, maxWidth: 160, px: 0 }}
                >
                  <Typography
                    variant="subtitle1"
                    color="text.secondary"
                    align="center"
                    sx={{ fontWeight: 600, px: 1 }}
                  >
                    {project.projectName}
                  </Typography>
                </TableCell>
              ))}

              <TableCell sx={{ width: 80, maxWidth: 80 }}>
                <Typography
                  variant="subtitle1"
                  color="text.secondary"
                  align="center"
                  sx={{ fontWeight: 600, px: 1 }}
                >
                  Unit
                </Typography>
              </TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {METRICS.map((m) => (
              <TableRow key={m.key} aria-label={m.key}>
                {/* Coluna da métrica */}
                <TableCell
                  sx={{
                    width: `calc(100% - ${projects.length * 160}px)`,
                    py: 1.25,
                    borderBottom: "1px solid #e0e0e0 !important",
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Typography variant="body2" sx={{ fontWeight: 500, width: 280 }}>
                      {m.label}
                      {m.tooltip && (
                        <StyledTooltip title={m.tooltip} arrow>
                          <span
                            className="icon"
                            style={{
                              width: 20,
                              height: 20,
                              marginLeft: 6,
                              backgroundRepeat: "no-repeat",
                              display: "inline-block",
                              verticalAlign: "middle",
                            }}
                          ></span>
                        </StyledTooltip>
                      )}
                    </Typography>
                  </Box>
                </TableCell>

                {/* Números por projeto */}
                {projects.map((project) => {
                  const raw =
                    (typeof m.valueFn === "function" ? m.valueFn(project) : undefined) ??
                    (m.valuePath ? getByPath(project, m.valuePath) : undefined);

                  const isNumber = typeof raw === "number" && Number.isFinite(raw);

                  return (
                    <TableCell
                      key={project.id ?? project.projectName}
                      align="right"
                      sx={{
                        width: 160,
                        minWidth: 160,
                        maxWidth: 160,
                        borderBottom: "1px solid #e0e0e0 !important",
                      }}
                    >
                      <Typography
                        variant="body2"
                        align="center"
                        noWrap
                        sx={{ fontVariantNumeric: "tabular-nums" }}
                        title={isNumber ? String(raw) : ""}
                      >
                        {isNumber ? roundToTwo(raw) : "--"}
                      </Typography>
                    </TableCell>
                  );
                })}

                {/* Unidade */}
                <TableCell
                  sx={{
                    width: 80,
                    maxWidth: 80,
                    px: 0,
                    borderBottom: "1px solid #e0e0e0 !important",
                  }}
                >
                  <Typography variant="body2" align="center" noWrap>
                    {m.unit}
                  </Typography>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* ---- Barras com escala por métrica ---- */}
      <GenericBars projects={projects} title="Health" forPdf={forPdf} />

    </Box>
  );
}